import { supabase, apiBaseUrl } from '../../lib/supabase';
import type { Database } from '../../types/supabase';

type Tables = Database['public']['Tables'];
type PolicyRow = Tables['policies']['Row'];
type OrganizationRow = Tables['organizations']['Row'];
type ProfileRow = Tables['profiles']['Row'];
type ChatConversationRow = Tables['ai_chat_conversations']['Row'];
type ChatMessageRow = Tables['ai_chat_messages']['Row'];

// Server-side writes: policy create/update/delete go through the Express
// API (server/routes/policy.ts) rather than straight to Supabase, so they
// get zod-validated shape/length caps and organization_id is derived from
// the verified caller instead of trusted from the client. GET reads stay
// direct against Supabase (RLS-scoped, nothing to validate on the way in).
async function policyApiRequest(path: string, init?: RequestInit) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in');

  const res = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      ...(init?.headers ?? {}),
    },
  });

  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(body?.error ?? `Request failed (${res.status})`);
  }
  return body;
}

// Policy Services
export const policyService = {
  async create(policy: Omit<PolicyRow, 'id' | 'created_at' | 'updated_at'>) {
    console.log('Creating policy in service:', policy);
    const { policy: data } = await policyApiRequest('/api/policies', {
      method: 'POST',
      body: JSON.stringify(policy),
    });
    console.log('Created policy in service:', data);
    return data;
  },

  async update(id: string, updates: Partial<PolicyRow>) {
    console.log('Updating policy in service:', { id, updates });
    const { policy: data } = await policyApiRequest(`/api/policies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    console.log('Updated policy in service:', data);
    return data;
  },

  async delete(id: string) {
    console.log('Deleting policy in service:', id);
    await policyApiRequest(`/api/policies/${id}`, { method: 'DELETE' });
    console.log('Deleted policy in service:', id);
  },

  async restore(id: string, versionNumber: number) {
    const { policy: data } = await policyApiRequest(`/api/policies/${id}/restore`, {
      method: 'POST',
      body: JSON.stringify({ versionNumber }),
    });
    return data;
  },

  async getById(id: string) {
    console.log('Getting policy by ID in service:', id);
    const { data, error } = await supabase
      .from('policies')
      .select()
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error getting policy:', error);
      throw error;
    }
    console.log('Got policy in service:', data);
    return data;
  },

  async getByOrganization(organizationId: string) {
    console.log('Getting policies by organization in service:', organizationId);
    const { data, error } = await supabase
      .from('policies')
      .select()
      .eq('organization_id', organizationId);

    if (error) {
      console.error('Error getting policies:', error);
      throw error;
    }
    console.log('Got policies in service:', data);
    return data;
  },
};

// Organization Services
export const organizationService = {
  async create(org: Omit<OrganizationRow, 'id' | 'created_at' | 'updated_at'>) {
    console.log('Creating organization in service:', org);
    const { data, error } = await supabase
      .from('organizations')
      .insert([org])
      .select()
      .single();

    if (error) {
      console.error('Error creating organization:', error);
      throw error;
    }
    console.log('Created organization in service:', data);
    return data;
  },

  async update(id: string, updates: Partial<OrganizationRow>) {
    console.log('Updating organization in service:', { id, updates });
    const { data, error } = await supabase
      .from('organizations')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating organization:', error);
      throw error;
    }
    console.log('Updated organization in service:', data);
    return data;
  },

  async getById(id: string) {
    console.log('Getting organization by ID in service:', id);
    const { data, error } = await supabase
      .from('organizations')
      .select()
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error getting organization:', error);
      throw error;
    }
    console.log('Got organization in service:', data);
    return data;
  },
};

// Profile Services
export const profileService = {
  async create(profile: Omit<ProfileRow, 'id' | 'created_at' | 'updated_at'>) {
    console.log('Creating profile in service:', profile);
    const { data, error } = await supabase
      .from('profiles')
      .insert([profile])
      .select()
      .single();

    if (error) {
      console.error('Error creating profile:', error);
      throw error;
    }
    console.log('Created profile in service:', data);
    return data;
  },

  async update(id: string, updates: Partial<ProfileRow>) {
    console.log('Updating profile in service:', { id, updates });
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('user_id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
    console.log('Updated profile in service:', data);
    return data;
  },

  async getById(id: string) {
    console.log('Getting profile by ID in service:', id);
    const { data, error } = await supabase
      .from('profiles')
      .select('*, organizations (*)')
      .eq('user_id', id)
      .single();

    if (error) {
      console.error('Error getting profile:', error);
      throw error;
    }
    console.log('Got profile in service:', data);
    return data;
  },
};

// AI Chat Services - conversations/messages are personal (RLS on
// auth.uid()), not org-scoped like policies, so these never take an
// organizationId filter the way policyService does.
export const chatService = {
  async listConversations(userId: string): Promise<ChatConversationRow[]> {
    const { data, error } = await supabase
      .from('ai_chat_conversations')
      .select()
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error listing chat conversations:', error);
      throw error;
    }
    return data;
  },

  async createConversation(userId: string, organizationId: string, title = 'New conversation'): Promise<ChatConversationRow> {
    const { data, error } = await supabase
      .from('ai_chat_conversations')
      .insert([{ user_id: userId, organization_id: organizationId, title }])
      .select()
      .single();

    if (error) {
      console.error('Error creating chat conversation:', error);
      throw error;
    }
    return data;
  },

  async updateConversationTitle(id: string, title: string): Promise<ChatConversationRow> {
    const { data, error } = await supabase
      .from('ai_chat_conversations')
      .update({ title })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating chat conversation title:', error);
      throw error;
    }
    return data;
  },

  async deleteConversation(id: string): Promise<void> {
    const { error } = await supabase
      .from('ai_chat_conversations')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting chat conversation:', error);
      throw error;
    }
  },

  async getMessages(conversationId: string): Promise<ChatMessageRow[]> {
    const { data, error } = await supabase
      .from('ai_chat_messages')
      .select()
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error getting chat messages:', error);
      throw error;
    }
    return data;
  },

  async addMessage(conversationId: string, role: 'user' | 'assistant', content: string): Promise<ChatMessageRow> {
    const { data, error } = await supabase
      .from('ai_chat_messages')
      .insert([{ conversation_id: conversationId, role, content }])
      .select()
      .single();

    if (error) {
      console.error('Error adding chat message:', error);
      throw error;
    }
    // Bump the conversation's updated_at so the sidebar sorts by recency;
    // best-effort, doesn't need to block the caller on failure.
    supabase
      .from('ai_chat_conversations')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', conversationId)
      .then(({ error: touchError }) => {
        if (touchError) console.error('Error touching conversation updated_at:', touchError);
      });
    return data;
  },
};

// Auth helper functions
export const getCurrentUser = async () => {
  console.log('Getting current user');
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) {
    console.error('Error getting current user:', error);
    throw error;
  }
  console.log('Got current user:', user);
  return user;
};

export const getCurrentProfile = async () => {
  console.log('Getting current profile');
  const user = await getCurrentUser();
  if (!user) {
    console.log('No current user found');
    return null;
  }
  
  // Get profile by auth user id
  const { data, error } = await supabase
    .from('profiles')
    .select('*, organizations (*)')
    .eq('user_id', user.id)
    .single();

  if (error) {
    console.error('Error getting current profile:', error);
    throw error;
  }
  console.log('Got current profile:', data);
  return data;
};
